/**
 * useWordEdits — detects writer-initiated edits to the live Word
 * document and reports them as EventLog 'word' entries.
 *
 * Spec 23 P2.3. The agent's WriteGate-mediated writes (Spec 21
 * Approval Gate family) are not writer-initiated and are NOT
 * captured here — those have their own signal path.
 *
 * # Mechanism — event-driven, gated by run state
 *
 * The first implementation loaded ``body.text`` on every cursor move — far
 * too heavy on long CSR drafts (the ctrl+A → backspace hang). The second
 * polled every 5 s, which on Word desktop is a Word round trip on Word's UI
 * thread every tick, all session long, whether or not anyone typed.
 *
 * The current flow:
 *   1. ``enabled`` flips on (run-active states: approval / paused /
 *      post-run). The hook takes a baseline and subscribes to writer
 *      edits (syncEngine's paragraph events) and settled selection
 *      changes (hosts without WordApi 1.6 paragraph events).
 *   2. Each trigger schedules ONE ``checkBodySignature`` after
 *      ``SETTLE_MS`` of quiet — one ``Word.run`` loading only first
 *      paragraph + last paragraph + cursor paragraph text (constant
 *      cost regardless of doc size). It is skipped while a Load or an
 *      add-in write owns Word (those changes are not the writer's).
 *   3. The hook compares against the prior tick. A diff fires
 *      ``onEdit`` once.
 *   4. ``enabled`` flips off → interval clears, baselines reset.
 *
 * # False-positive guards
 *
 *   - Cursor-paragraph identity: the cursor paragraph's ``text`` is
 *     only meaningful relative to the SAME paragraph across ticks.
 *     When the writer clicks or highlights into a different paragraph,
 *     the cursor paragraph's text changes — but that's not an edit,
 *     it's a navigation. We track the paragraph's ``uniqueLocalId``
 *     (loaded by ``checkBodySignature`` when the host supports it) and
 *     only treat a text diff as an edit when the id matches the prior
 *     tick. Cursor-moved-to-different-paragraph silently rebaselines.
 *     On hosts without ``uniqueLocalId`` (WordApi < 1.6 and no
 *     WordApiDesktop), we drop the cursor signal entirely and rely on
 *     first/last paragraph diffs. Strictly fewer false positives,
 *     slightly fewer true positives (mid-doc edits between first/last
 *     can be missed) — acceptable given the docstring's coalescing
 *     trade-off.
 *
 *   - Agent-write quiet window: the gate enables this hook during
 *     ``approval``/``paused``/``post-run``, but queued agent writes
 *     (``insertSectionContent`` / ``updateTitlePageFields``) can still
 *     flush during those windows and shift first/last paragraph text.
 *     Callers pass ``agentWriteRef`` whose ``.current`` is stamped to
 *     ``Date.now()`` after each agent write. If the stamp is within
 *     ``QUIET_WINDOW_MS`` of a tick, we silently rebaseline and skip
 *     the fire path.
 *
 * Trade-offs:
 *   - Edits within the settle window are coalesced into one
 *     ``onEdit`` fire. The snippet captures the *latest* cursor
 *     paragraph at observation time, not every intermediate state.
 *   - Pure word-swaps that don't touch first/last paragraph length
 *     and that the cursor doesn't enter are missed. Same trade-off
 *     the prior implementation accepted (which only saw the cursor
 *     paragraph anyway).
 */

import { type MutableRefObject, useEffect, useRef } from "react";
import { isLiveDocLoading } from "../../services/liveDocLoadState";
import { subscribeParagraphEdits } from "../../services/syncEngine";
import { queueWordRead } from "../../services/wordReadQueue";
import { checkBodySignature, subscribeSelectionSettled } from "../../services/wordProbe";
import { isWordWriteQueueBusy } from "../../services/wordWriteQueue";

/** Quiet time after the last edit / selection trigger before the hook reads
 *  Word once. */
const SETTLE_MS = 3000;

/** Window (ms) after an agent write during which the hook silently
 *  rebaselines instead of firing. Comfortably > ``SETTLE_MS`` so a single
 *  agent write can't trip the next check. */
const QUIET_WINDOW_MS = 8000;

// ---------------------------------------------------------------------------
// Pure helpers — testable in isolation
// ---------------------------------------------------------------------------

/**
 * Deterministic content hash. djb2-style — fast, zero-dep, collision
 * rate is fine for paragraph-scoped change detection.
 * NOT cryptographic; don't reuse for security.
 */
export function hashBodyText(text: string): string {
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
  }
  return `djb2:${(hash >>> 0).toString(16)}`;
}

/** Truncate a paragraph's text to a snippet shown in the WordDetail panel.
 *  Whitespace is collapsed; an ellipsis is appended only when truncating. */
export function paragraphSnippet(text: string, max = 160): string | null {
  if (!text) return null;
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed.length === 0) return null;
  return collapsed.length > max ? `${collapsed.slice(0, max).trimEnd()}…` : collapsed;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseWordEditsOptions {
  /** Gate — hook is inert when false. Typically ``state.state ===
   *  "approval" || "paused" || "post-run"``. Shell flips this on/off. */
  enabled: boolean;
  /** Called on each detected edit moment. Snippet is the truncated
   *  text of the cursor's paragraph at the moment of detection
   *  (null when the paragraph was empty / unreadable). */
  onEdit: (ts: number, snippet: string | null) => void;
  /** Optional ref whose ``.current`` is the ``Date.now()`` of the most
   *  recent agent write to the document body. Used to suppress firing
   *  during the quiet window after agent writes — see file docstring. */
  agentWriteRef?: MutableRefObject<number>;
}

interface CursorBaseline {
  id: string | null;
  hash: string;
}

export function useWordEdits(opts: UseWordEditsOptions): void {
  const { enabled, onEdit, agentWriteRef } = opts;

  const onEditRef = useRef(onEdit);
  const agentWriteRefRef = useRef<MutableRefObject<number> | undefined>(agentWriteRef);
  const priorFirstRef = useRef<string | null>(null);
  const priorLastRef = useRef<string | null>(null);
  const priorCursorRef = useRef<CursorBaseline | null>(null);

  useEffect(() => {
    onEditRef.current = onEdit;
  }, [onEdit]);

  useEffect(() => {
    agentWriteRefRef.current = agentWriteRef;
  }, [agentWriteRef]);

  useEffect(() => {
    if (!enabled) {
      priorFirstRef.current = null;
      priorLastRef.current = null;
      priorCursorRef.current = null;
      return;
    }
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      // A Load or add-in write owns Word: its changes aren't the writer's, and
      // a read now would queue behind it on Word desktop's UI thread.
      if (inFlight || isLiveDocLoading() || isWordWriteQueueBusy()) return;
      inFlight = true;
      let sig: Awaited<ReturnType<typeof checkBodySignature>>;
      try {
        sig = await queueWordRead("wordEdits:signature", checkBodySignature);
      } finally {
        inFlight = false;
      }
      if (cancelled) return;
      const cursorHash = hashBodyText(sig.cursorParaText);
      const cursorId = sig.cursorParaId;

      const isBaseline =
        priorFirstRef.current === null || priorLastRef.current === null || priorCursorRef.current === null;

      const firstChanged = priorFirstRef.current !== null && sig.firstParaText !== priorFirstRef.current;
      const lastChanged = priorLastRef.current !== null && sig.lastParaText !== priorLastRef.current;

      // Cursor diff is only meaningful when we can attribute the prior
      // sample to the SAME paragraph as the current sample. Without that
      // attribution (host without uniqueLocalId, or one side null), drop
      // the signal entirely — strictly safer than the old behavior, which
      // treated any cursor-paragraph text change as an edit.
      const prior = priorCursorRef.current;
      const cursorSameParagraph = prior !== null && prior.id !== null && cursorId !== null && prior.id === cursorId;
      const cursorChanged = cursorSameParagraph && prior !== null && cursorHash !== prior.hash;

      const inQuietWindow =
        agentWriteRefRef.current !== undefined && Date.now() - agentWriteRefRef.current.current < QUIET_WINDOW_MS;

      if (!isBaseline && !inQuietWindow && (firstChanged || lastChanged || cursorChanged)) {
        onEditRef.current(Date.now(), paragraphSnippet(sig.cursorParaText));
      }

      priorFirstRef.current = sig.firstParaText;
      priorLastRef.current = sig.lastParaText;
      priorCursorRef.current = { id: cursorId, hash: cursorHash };
    };
    void tick();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void tick();
      }, SETTLE_MS);
    };
    const unsubEdits = subscribeParagraphEdits(schedule);
    const unsubSelection = subscribeSelectionSettled(schedule, SETTLE_MS);
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      unsubEdits();
      unsubSelection();
    };
  }, [enabled]);
}
